
import { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from './use-mobile';

interface ImageOptimizationOptions {
  priority?: boolean;
  lazy?: boolean;
  quality?: number;
  maxWidth?: number;
}

export const useImageOptimization = () => {
  const isMobile = useIsMobile();
  const [supportedFormats, setSupportedFormats] = useState({
    webp: false,
    avif: false
  });

  // Détecter les formats d'image supportés
  useEffect(() => {
    const checkWebP = () => {
      const webp = new Image();
      webp.onload = webp.onerror = () => {
        setSupportedFormats(prev => ({ ...prev, webp: webp.height === 2 }));
      };
      webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    };

    checkWebP();
  }, []);

  const optimizeImageUrl = useCallback((originalUrl: string, options: ImageOptimizationOptions = {}) => {
    const { quality = 75, maxWidth, priority = false } = options;
    
    // Optimisation agressive basée sur la taille réelle nécessaire
    const effectiveMaxWidth = maxWidth || (isMobile ? 600 : 1400);
    const effectiveQuality = isMobile ? (priority ? 60 : 55) : quality;
    
    // Si c'est une image Supabase ou Lovable, ajouter des paramètres d'optimisation
    if (originalUrl.includes('supabase.co/storage') || originalUrl.includes('/lovable-uploads/')) {
      const params = new URLSearchParams();
      params.set('quality', effectiveQuality.toString());
      params.set('width', effectiveMaxWidth.toString());
      params.set('format', 'webp');
      params.set('resize', 'cover');
      
      if (originalUrl.includes('?')) {
        return `${originalUrl}&${params.toString()}`;
      } else {
        return `${originalUrl}?${params.toString()}`;
      }
    }
    
    return originalUrl;
  }, [isMobile, supportedFormats]);

  const preloadCriticalImages = useCallback((imageUrls: string[]) => {
    if (typeof window === 'undefined') return;

    // Sur mobile, ne précharger que l'image hero avec compression optimisée
    const urlsToPreload = isMobile ? imageUrls.slice(0, 1) : imageUrls.slice(0, 2);
    
    urlsToPreload.forEach((url, index) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = optimizeImageUrl(url, { priority: index === 0, quality: isMobile ? 45 : 75 });
      
      if (index === 0) {
        link.setAttribute('fetchpriority', 'high');
        link.setAttribute('importance', 'high');
      }
      
      // Insérer en premier pour priorité maximale
      const firstChild = document.head.firstElementChild;
      if (firstChild) {
        document.head.insertBefore(link, firstChild);
      } else {
        document.head.appendChild(link);
      }
    });
  }, [isMobile, optimizeImageUrl]);

  return {
    optimizeImageUrl,
    preloadCriticalImages,
    supportedFormats,
    isMobile
  };
};
